import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Article,
  Contractor,
  Contract,
  Department,
  Employee,
  PlanfactOperation,
  Task,
  TaskClosingStatus,
  TaskUpdatePayload,
  WorkType,
  getArticles,
  getContractors,
  getContracts,
  getContractFormAssignees,
  getEmployees,
  getExecutorDepartments,
  getExecutorDepartmentEmployees,
  getPlanfactOperations,
  getTask,
  getTasks,
  getWorkTypes,
  getContractStages,
  updateTask,
  type ContractStage,
} from '../services/api';
import {
  formatContractSumDisplay,
  formatDate,
  formatDateShort,
  normalizeContractSumDraft,
  parseContractSumInput,
} from '../utils/table';
import { formatFinanceCell } from '../utils/taskFinance';
import { getTaskStatusLabel, taskStatusBadgeVariant } from '../utils/taskStatus';
import { useAuth } from '../context/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import TaskExecutorsPanel, { ExecutorsDraft, type PeerExecutorInDept } from '../components/TaskExecutorsPanel';
import { datesFromBoundStages, StageBindPicker } from '../components/ContractStagesTable';
import PaymentDonut from '../components/PaymentDonut';
import BudgetProgressBar from '../components/BudgetProgressBar';
import BalanceBarChart from '../components/BalanceBarChart';
import {
  BUDGET_EXTRA_EXPENSES_KEY,
  buildMarginCalcHeaderCells,
  buildMarginCalcTableData,
  buildMarginFinanceRows,
  sortMarginCalcColumns,
  sumTasksPlannedExpenses,
  taskMarginFieldValue,
} from '../utils/marginCalc';
import MarginCalcTable from '../components/MarginCalcTable';
import TaskBalanceTable, { type TaskBalanceCellClick } from '../components/TaskBalanceTable';
import TaskOperationsTable from '../components/TaskOperationsTable';
import TaskDeadlinesGantt from '../components/TaskDeadlinesGantt';
import { formatDeadlineRelativeTo } from '../utils/deadline';
import { expenseAmountMagnitude, isOperationExpense } from '../utils/operationBudgetArticle';
import { resolveContractFamily } from '../utils/contractFamily';
import { sumFamilyTaskActualExpensesFromOperations } from '../utils/contractReceipts';
import {
  buildTaskIdDeadlinesData,
  resolveFamilyStageClosingSummary,
  resolveFamilyStageReferenceDate,
  type DeadlineStatusVariant,
} from '../utils/contractDeadlines';
import {
  ALL_TASK_EXPENSE_EXTRAS,
  TASK_BALANCE_EXPENSES_ROW_ID,
  buildTaskBalanceTable,
  buildTaskExpensesTable,
  collectOperationsForBalanceCell,
  collectOperationsForExpensesCell,
  excludeHiddenExpenseCategoryOperations,
  filterTaskOperations,
  operationContributesExpenseExtras,
  type TaskBalanceColumnKey,
} from '../utils/taskBalance';
import '../styles/DataPage.css';
import '../styles/ContractorDetailPage.css';
import './TaskDetailPage.css';
import './ContractDetailPage.css';

type TaskCardTabId = 'general' | 'executors' | 'budget' | 'expenses' | 'balance' | 'deadlines';

type TaskOpsViewFilter =
  | { kind: 'all' }
  | {
      kind: 'balanceCell';
      rowId: string;
      rowLabel: string;
      columnKey: TaskBalanceColumnKey;
      columnLabel: string;
    };

function deadlineStatusBadgeClass(variant: DeadlineStatusVariant): string {
  return `contract-detail__deadline-badge contract-detail__deadline-badge--${variant}`;
}

function deadlineDeltaCellClass(): string {
  return 'task-detail__deadlines-delta task-detail__deadlines-date';
}

function formatSignedBalance(value: number): string {
  if (value > 0) {
    return `+${formatContractSumDisplay(value)}`;
  }
  return formatContractSumDisplay(value);
}

function formatFactExpensesTotal(value: number): string {
  return formatContractSumDisplay(expenseAmountMagnitude(value));
}

type ExpensesTabBanner =
  | { overPlan: false; kind: 'spent-only'; spent: string }
  | { overPlan: false; kind: 'equal'; spent: string }
  | {
      overPlan: boolean;
      kind: 'diff';
      spent: string;
      diff: string;
      percent: string | null;
      direction: 'меньше' | 'больше';
    };

function normalizeExpensePlan(plan: number | null | undefined): number | null {
  if (plan == null || plan === 0) {
    return null;
  }
  return plan;
}

function formatExpensesTabBanner(spentSigned: number, plan: number | null): ExpensesTabBanner {
  const spent = Math.abs(spentSigned);
  const spentLabel = formatContractSumDisplay(spent);
  const effectivePlan = normalizeExpensePlan(plan);
  if (effectivePlan == null) {
    return { kind: 'spent-only', spent: spentLabel, overPlan: false };
  }
  const diff = spent - effectivePlan;
  if (Math.abs(diff) < 0.5) {
    return { kind: 'equal', spent: spentLabel, overPlan: false };
  }
  const direction = diff > 0 ? 'больше' : 'меньше';
  return {
    kind: 'diff',
    spent: spentLabel,
    diff: formatContractSumDisplay(Math.abs(diff)),
    percent: `${Math.round((Math.abs(diff) / Math.abs(effectivePlan)) * 100)}%`,
    direction,
    overPlan: diff > 0,
  };
}

function taskDetailPath(task: Pick<Task, 'id' | 'contracts_task_id'>): string {
  return `/tasks/${task.id}?contracts_task_id=${task.contracts_task_id}`;
}

/** Поля только из GET /tasks/{id}; при слиянии со строкой списка их нельзя терять. */
function mergeTaskDetailFields(detail: Task, display: Task): Task {
  return {
    ...display,
    payment_paid: detail.payment_paid,
    actual_expenses: detail.actual_expenses,
    closing_statuses: detail.closing_statuses ?? display.closing_statuses,
  };
}

function minTaskDate(values: Array<string | null | undefined>): string | null {
  let min: string | null = null;
  for (const raw of values) {
    const v = raw?.trim();
    if (!v) continue;
    if (min == null || v < min) min = v;
  }
  return min;
}

function maxTaskDate(values: Array<string | null | undefined>): string | null {
  let max: string | null = null;
  for (const raw of values) {
    const v = raw?.trim();
    if (!v) continue;
    if (max == null || v > max) max = v;
  }
  return max;
}

function assigneesGroupedByDepartment(
  assigneeIds: number[],
  employeesByDepartment: Map<number, Employee[]>,
): Map<number, Employee[]> {
  const assigneeSet = new Set(assigneeIds);
  const result = new Map<number, Employee[]>();
  for (const [deptId, deptEmployees] of employeesByDepartment) {
    const matched = deptEmployees.filter((emp) => assigneeSet.has(emp.id));
    if (matched.length > 0) {
      result.set(deptId, matched);
    }
  }
  return result;
}

interface ContractsTaskFinanceDraft {
  amount: string;
  planned_cost: string;
  taxes: string;
  service_fee: string;
  partner_commission: string;
  planned_margin: string;
  adjustment: string;
}

interface ContractsTaskEntryDraft {
  contract_id: number;
  work_type_id: number | null;
  finance: ContractsTaskFinanceDraft;
  deadlines: {
    start_date: string;
    end_date: string;
  };
  contract_stage_ids: number[];
}

interface TaskCardDraft {
  manager_id: number | null;
  project_lead_id: number | null;
  department_ids: number[];
  assignee_employee_ids: number[];
  byContractsTask: Record<number, ContractsTaskEntryDraft>;
}

type FinanceDraftKey = keyof ContractsTaskFinanceDraft;

const DRAFT_KEY_BY_ROW: Record<string, FinanceDraftKey> = {
  amount: 'amount',
  planned_cost: 'planned_cost',
  taxes: 'taxes',
  service_fee: 'service_fee',
  partner_commission: 'partner_commission',
  planned_margin: 'planned_margin',
  adjustment: 'adjustment',
};

function buildCardDraftFromTask(task: Task, siblings: Task[]): TaskCardDraft {
  const rows = siblings.length > 0 ? siblings : [task];
  const byContractsTask: Record<number, ContractsTaskEntryDraft> = {};
  for (const row of rows) {
    byContractsTask[row.contracts_task_id] = {
      contract_id: row.contract_id,
      work_type_id: row.work_type_id,
      finance: {
        amount: numToDraft(row.amount),
        planned_cost: numToDraft(row.planned_cost),
        taxes: numToDraft(row.taxes),
        service_fee: numToDraft(row.service_fee),
        partner_commission: numToDraft(row.partner_commission),
        planned_margin: numToDraft(row.planned_margin),
        adjustment: numToDraft(row.adjustment),
      },
      deadlines: {
        start_date: row.start_date ?? '',
        end_date: row.end_date ?? '',
      },
      contract_stage_ids: [...(row.contract_stage_ids ?? [])],
    };
  }
  return {
    manager_id: task.manager_id ?? null,
    project_lead_id: task.project_lead_id ?? null,
    department_ids: [...(task.department_ids ?? [])],
    assignee_employee_ids: [...(task.assignee_employee_ids ?? [])],
    byContractsTask,
  };
}

function executorsFromDraft(draft: TaskCardDraft): ExecutorsDraft {
  return {
    department_ids: draft.department_ids,
    assignee_employee_ids: draft.assignee_employee_ids,
  };
}

function numToDraft(value: number | string | undefined | null): string {
  if (value == null || value === '') return '';
  return String(value);
}

function parseOptionalNumber(value: string): number | undefined {
  return parseContractSumInput(value);
}

function sortEmployeesByName(list: Employee[]): Employee[] {
  return [...list].sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru'));
}

function employeesForRoleSelect(
  roleEmployees: Employee[],
  selectedId: number | null,
  allEmployees: Employee[],
): Employee[] {
  const byId = new Map(roleEmployees.map((e) => [e.id, e]));
  if (selectedId != null && !byId.has(selectedId)) {
    const selected = allEmployees.find((e) => e.id === selectedId);
    if (selected) {
      byId.set(selected.id, selected);
    }
  }
  return sortEmployeesByName([...byId.values()]);
}

interface ClosingStatusLinesProps {
  rows: TaskClosingStatus[];
}

const ClosingStatusLines: React.FC<ClosingStatusLinesProps> = ({ rows }) => {
  if (rows.length === 0) {
    return <div className="task-detail__closing-empty">—</div>;
  }
  return (
    <div className="task-detail__closing-lines">
      {rows.map((row) => {
        const done = Boolean(row.closing_date);
        return (
          <div key={row.stage_id} className="task-detail__closing-line">
            <span className="task-detail__closing-line-name">{row.stage_name}</span>
            <span className="task-detail__closing-line-date">
              {done ? formatDate(row.closing_date ?? undefined) : 'нет'}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const TaskDetailPage: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { can, user, isAdmin, hasDepartment, expensePlanfactArticles } = useAuth();

  const canTasks = can('TASKS', 'VIEW');
  const canEditTask = can('TASKS', 'EDIT');
  const canContractors = can('CONTRACTORS', 'VIEW');
  const canSum = can('CONTRACT_SUM', 'VIEW');
  const canMargin = can('CONTRACT_MARGIN', 'VIEW');
  const canIncome = can('INCOME', 'VIEW');
  const canOutcome = can('OUTCOME', 'VIEW');
  const canOutcomeCash = isAdmin || can('OUTCOME_CASH', 'VIEW');
  const canOutcomeServ = isAdmin || can('OUTCOME_SERV', 'VIEW');
  const canOutcomeTax = isAdmin || can('OUTCOME_TAX', 'VIEW');
  const canOutcomePartner = isAdmin || can('OUTCOME_PARTNER', 'VIEW');
  const canOutcomePlan = can('OUTCOME_PLAN', 'VIEW');
  const canCashFlow = canSum && canIncome && canOutcome && canMargin;
  const canBalanceTab = canCashFlow;
  const canExpensesTab = canOutcome;
  const canBudgetTab = canMargin && canSum;
  const canLoadOperations = canBalanceTab || canExpensesTab;

  const taskPk = idParam ? parseInt(idParam, 10) : NaN;

  const [task, setTask] = useState<Task | null>(null);
  const [allContracts, setAllContracts] = useState<Contract[]>([]);
  const [allContractors, setAllContractors] = useState<Contractor[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [managerEmployees, setManagerEmployees] = useState<Employee[]>([]);
  const [projectLeadEmployees, setProjectLeadEmployees] = useState<Employee[]>([]);
  const [executorDepartments, setExecutorDepartments] = useState<Department[]>([]);
  const [deptEmployeeIds, setDeptEmployeeIds] = useState<Record<number, number[]>>({});
  const [expandedContractIds, setExpandedContractIds] = useState<Set<number>>(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useDocumentTitle(task?.short_name || task?.task_id || 'Задача');

  const [headerName, setHeaderName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState('');

  const [activeCardTab, setActiveCardTab] = useState<TaskCardTabId>('general');
  const [deadlinesTasksExpanded, setDeadlinesTasksExpanded] = useState(false);
  const [cardEditing, setCardEditing] = useState(false);
  const [cardDraft, setCardDraft] = useState<TaskCardDraft | null>(null);
  const [cardSaving, setCardSaving] = useState(false);
  const [cardError, setCardError] = useState('');
  const [stagesByContractId, setStagesByContractId] = useState<Record<number, ContractStage[]>>({});
  const [planfactOperations, setPlanfactOperations] = useState<PlanfactOperation[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [opsViewFilter, setOpsViewFilter] = useState<TaskOpsViewFilter>({ kind: 'all' });

  const toggleContract = (contractId: number): void => {
    setExpandedContractIds((prev) => {
      const next = new Set(prev);
      if (next.has(contractId)) next.delete(contractId);
      else next.add(contractId);
      return next;
    });
  };

  const employeeById = useMemo(() => {
    const map = new Map<number, Employee>();
    for (const employee of employees) {
      map.set(employee.id, employee);
    }
    for (const employee of managerEmployees) {
      map.set(employee.id, employee);
    }
    for (const employee of projectLeadEmployees) {
      map.set(employee.id, employee);
    }
    return map;
  }, [employees, managerEmployees, projectLeadEmployees]);

  const getEmployeeName = useCallback(
    (id?: number | null): string => {
      if (id == null) return '—';
      return employeeById.get(id)?.full_name || '—';
    },
    [employeeById],
  );

  const workTypeNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const wt of workTypes) {
      map.set(wt.id, wt.name);
    }
    return map;
  }, [workTypes]);

  const workTypeLabel = useCallback(
    (id: number | null | undefined): string => {
      if (id == null) return '—';
      return workTypeNameById.get(id) ?? '—';
    },
    [workTypeNameById],
  );

  const load = useCallback(async (): Promise<void> => {
    if (!canTasks || Number.isNaN(taskPk)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const taskRow = await getTask(taskPk);
      const contractsTaskIdParam = searchParams.get('contracts_task_id');
      const parsedContractsTaskId = contractsTaskIdParam
        ? parseInt(contractsTaskIdParam, 10)
        : NaN;

      try {
        const [emps, depts, deptEmps, assignees, types] = await Promise.all([
          getEmployees(),
          getExecutorDepartments(),
          getExecutorDepartmentEmployees(),
          getContractFormAssignees(),
          getWorkTypes(),
        ]);
        setEmployees(emps);
        setManagerEmployees(assignees.managers);
        setProjectLeadEmployees(assignees.project_leads);
        setExecutorDepartments(depts);
        setWorkTypes(types);
        const idsByDept: Record<number, number[]> = {};
        for (const item of deptEmps) {
          idsByDept[item.department_id] = item.employee_ids;
        }
        setDeptEmployeeIds(idsByDept);
      } catch {
        setEmployees([]);
        setManagerEmployees([]);
        setProjectLeadEmployees([]);
        setExecutorDepartments([]);
        setDeptEmployeeIds({});
      }

      try {
        const tasks = await getTasks();
        setAllTasks(tasks);
        let displayTask = taskRow;
        if (!Number.isNaN(parsedContractsTaskId)) {
          const fromList = tasks.find(
            (row) => row.id === taskPk && row.contracts_task_id === parsedContractsTaskId,
          );
          if (fromList) {
            displayTask = mergeTaskDetailFields(taskRow, fromList);
          }
        } else {
          const fromList = tasks.find((row) => row.id === taskPk);
          if (fromList) {
            displayTask = mergeTaskDetailFields(taskRow, fromList);
          }
        }
        setTask(displayTask);
        setHeaderName(displayTask.short_name);
      } catch {
        setAllTasks([]);
        setTask(taskRow);
        setHeaderName(taskRow.short_name);
      }

      try {
        const contracts = await getContracts();
        setAllContracts(contracts);
      } catch {
        setAllContracts([]);
      }

      try {
        const contractors = await getContractors();
        setAllContractors(contractors);
      } catch {
        setAllContractors([]);
      }

      try {
        const [operations, articleRows] = await Promise.all([
          canLoadOperations
            ? getPlanfactOperations().catch(() => [] as PlanfactOperation[])
            : Promise.resolve([] as PlanfactOperation[]),
          canLoadOperations
            ? getArticles().catch(() => [] as Article[])
            : Promise.resolve([] as Article[]),
        ]);
        setPlanfactOperations(operations);
        setArticles(articleRows);
      } catch {
        setPlanfactOperations([]);
        setArticles([]);
      }
    } catch (e) {
      setError('Не удалось загрузить задачу');
      setTask(null);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [canTasks, canLoadOperations, taskPk, searchParams]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setCardEditing(false);
    setCardDraft(null);
    setCardError('');
    setActiveCardTab('general');
    setOpsViewFilter({ kind: 'all' });
  }, [taskPk]);

  const taskShortName = task?.short_name;
  useEffect(() => {
    if (taskShortName != null) {
      setHeaderName(taskShortName);
    }
  }, [task?.id, taskShortName]);

  const managerSelectOptions = useMemo(
    () =>
      employeesForRoleSelect(
        managerEmployees,
        cardEditing && cardDraft ? cardDraft.manager_id : task?.manager_id ?? null,
        employees,
      ),
    [managerEmployees, employees, cardEditing, cardDraft, task?.manager_id],
  );

  const projectLeadSelectOptions = useMemo(
    () =>
      employeesForRoleSelect(
        projectLeadEmployees,
        cardEditing && cardDraft ? cardDraft.project_lead_id : task?.project_lead_id ?? null,
        employees,
      ),
    [projectLeadEmployees, employees, cardEditing, cardDraft, task?.project_lead_id],
  );

  const employeesByDepartment = useMemo(() => {
    const byId = new Map(employees.map((e) => [e.id, e]));
    const result = new Map<number, Employee[]>();
    for (const dept of executorDepartments) {
      const ids = deptEmployeeIds[dept.id] ?? [];
      const list = ids
        .map((id) => byId.get(id))
        .filter((e): e is Employee => e != null)
        .sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru'));
      result.set(dept.id, list);
    }
    return result;
  }, [executorDepartments, employees, deptEmployeeIds]);

  const contractorsById = useMemo(() => {
    const map = new Map<number, Contractor>();
    for (const c of allContractors) {
      map.set(c.id, c);
    }
    return map;
  }, [allContractors]);

  const contractsById = useMemo(() => {
    const map = new Map<number, Contract>();
    for (const c of allContracts) {
      map.set(c.id, c);
    }
    return map;
  }, [allContracts]);

  const taskIdSiblingTasks = useMemo((): Task[] => {
    if (!task) return [];
    let siblings = allTasks.filter((row) => row.task_id === task.task_id);
    if (siblings.length === 0) {
      siblings = [task];
    }
    return siblings;
  }, [task, allTasks]);

  useEffect(() => {
    const ids = new Set(taskIdSiblingTasks.map((row) => row.contract_id));
    const anchorId = task?.contract_id ?? taskIdSiblingTasks[0]?.contract_id ?? null;
    const anchor = anchorId != null ? contractsById.get(anchorId) : null;
    if (anchor) {
      const family = resolveContractFamily(anchor, allContracts);
      for (const ct of family.familyContracts) ids.add(ct.id);
    }
    const idList = [...ids];
    if (idList.length === 0) {
      setStagesByContractId({});
      return;
    }
    let cancelled = false;
    void Promise.all(
      idList.map(async (id) => {
        const rows = await getContractStages(id).catch(() => [] as ContractStage[]);
        return [id, rows] as const;
      }),
    ).then((pairs) => {
      if (cancelled) return;
      const next: Record<number, ContractStage[]> = {};
      for (const [id, rows] of pairs) next[id] = rows;
      setStagesByContractId(next);
    });
    return () => {
      cancelled = true;
    };
  }, [taskIdSiblingTasks, task?.contract_id, contractsById, allContracts]);

  const taskWorkTypesLabel = useMemo((): string => {
    const names = new Set<string>();
    for (const row of taskIdSiblingTasks) {
      const label = workTypeLabel(row.work_type_id);
      if (label !== '—') {
        names.add(label);
      }
    }
    if (names.size === 0) return '—';
    return [...names].sort((a, b) => a.localeCompare(b, 'ru')).join(', ');
  }, [taskIdSiblingTasks, workTypeLabel]);

  const activeContractsTaskId = useMemo((): number | null => {
    const fromUrl = searchParams.get('contracts_task_id');
    if (fromUrl) {
      const parsed = parseInt(fromUrl, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return task?.contracts_task_id ?? null;
  }, [searchParams, task?.contracts_task_id]);

  const activeContractTask = useMemo((): Task | null => {
    if (!task) return null;
    if (activeContractsTaskId == null) return task;
    return (
      taskIdSiblingTasks.find((row) => row.contracts_task_id === activeContractsTaskId) ?? task
    );
  }, [task, activeContractsTaskId, taskIdSiblingTasks]);

  const displayedContractId = useMemo<number | null>(() => {
    return activeContractTask?.contract_id ?? task?.contract_id ?? null;
  }, [activeContractTask, task?.contract_id]);

  const displayedContract = useMemo<Contract | null>(() => {
    if (displayedContractId == null) return null;
    return contractsById.get(displayedContractId) ?? null;
  }, [contractsById, displayedContractId]);

  const canViewAllTaskExpenseArticles = useMemo((): boolean => {
    if (hasDepartment('ALL_COMP')) {
      return true;
    }
    if (user == null) {
      return false;
    }
    const managerId = task?.manager_id ?? displayedContract?.manager_id ?? null;
    const projectLeadId = task?.project_lead_id ?? displayedContract?.project_lead_id ?? null;
    return user.id === managerId || user.id === projectLeadId;
  }, [
    hasDepartment,
    user,
    task?.manager_id,
    task?.project_lead_id,
    displayedContract?.manager_id,
    displayedContract?.project_lead_id,
  ]);

  const expenseArticleFilter = useMemo((): Set<string> | null => {
    if (canViewAllTaskExpenseArticles) {
      return null;
    }
    return new Set(expensePlanfactArticles);
  }, [canViewAllTaskExpenseArticles, expensePlanfactArticles]);

  const handleNameBlur = useCallback(async (): Promise<void> => {
    if (!canEditTask || !task) return;
    const trimmed = headerName.trim();
    if (trimmed === task.short_name) return;
    if (!trimmed) {
      setHeaderName(task.short_name);
      return;
    }
    setNameSaving(true);
    setNameError('');
    try {
      const updated = await updateTask(task.id, { short_name: trimmed });
      setTask(updated);
      setHeaderName(updated.short_name);
    } catch (e) {
      setHeaderName(task.short_name);
      setNameError(e instanceof Error ? e.message : 'Не удалось сохранить название');
    } finally {
      setNameSaving(false);
    }
  }, [canEditTask, task, headerName]);

  const handleStartCardEdit = useCallback((): void => {
    if (!task || !canEditTask) return;
    setCardDraft(buildCardDraftFromTask(task, taskIdSiblingTasks));
    setCardEditing(true);
    setCardError('');
  }, [task, taskIdSiblingTasks, canEditTask]);

  const handleCancelCardEdit = useCallback((): void => {
    setCardEditing(false);
    setCardDraft(null);
    setCardError('');
  }, []);

  const handleSaveCardEdit = useCallback(async (): Promise<void> => {
    if (!task || cardDraft == null) return;

    const columns = sortMarginCalcColumns(
      taskIdSiblingTasks.length > 0 ? taskIdSiblingTasks : [task],
      contractsById,
      workTypeLabel,
    );
    for (const column of columns) {
      const entry = cardDraft.byContractsTask[column.contracts_task_id];
      if (!entry) {
        setCardError('Не удалось собрать данные по связям задачи');
        return;
      }
      if (entry.work_type_id == null) {
        setCardError('Укажите тип задачи');
        return;
      }
      const stages = stagesByContractId[entry.contract_id] ?? [];
      if (stages.length > 0) {
        if ((entry.contract_stage_ids ?? []).length === 0) {
          setCardError('Привяжите задачу хотя бы к одному этапу работ');
          setActiveCardTab('deadlines');
          return;
        }
      } else if (!entry.deadlines.start_date.trim()) {
        setCardError('Укажите дату начала на вкладке «Сроки»');
        return;
      }
    }

    setCardSaving(true);
    setCardError('');
    try {
      for (let index = 0; index < columns.length; index += 1) {
        const column = columns[index];
        const entry = cardDraft.byContractsTask[column.contracts_task_id];
        const stages = stagesByContractId[entry.contract_id] ?? [];
        const payload: TaskUpdatePayload = {
          contract_id: entry.contract_id,
          work_type_id: entry.work_type_id ?? undefined,
          amount: parseOptionalNumber(entry.finance.amount),
          planned_cost: parseOptionalNumber(entry.finance.planned_cost),
          taxes: parseOptionalNumber(entry.finance.taxes),
          service_fee: parseOptionalNumber(entry.finance.service_fee),
          partner_commission: parseOptionalNumber(entry.finance.partner_commission),
          planned_margin: parseOptionalNumber(entry.finance.planned_margin),
          adjustment: parseOptionalNumber(entry.finance.adjustment),
        };
        if (stages.length > 0) {
          payload.contract_stage_ids = entry.contract_stage_ids ?? [];
        } else {
          payload.start_date = entry.deadlines.start_date.trim();
          payload.end_date = entry.deadlines.end_date.trim() || undefined;
        }
        if (index === 0) {
          payload.manager_id = cardDraft.manager_id;
          payload.project_lead_id = cardDraft.project_lead_id;
          payload.department_ids = cardDraft.department_ids;
          payload.assignee_employee_ids = cardDraft.assignee_employee_ids;
        }
        await updateTask(task.id, payload);
      }

      const [refreshed, tasks] = await Promise.all([getTask(task.id), getTasks()]);
      const fromList = tasks.find(
        (row) =>
          row.id === task.id
          && row.contracts_task_id === (activeContractsTaskId ?? refreshed.contracts_task_id),
      );
      setTask(fromList ? mergeTaskDetailFields(refreshed, fromList) : refreshed);
      setAllTasks(tasks);
      try {
        const contracts = await getContracts();
        setAllContracts(contracts);
      } catch {
        /* оставляем прежний список договоров */
      }
      setCardEditing(false);
      setCardDraft(null);
    } catch (e) {
      setCardError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setCardSaving(false);
    }
  }, [task, activeContractsTaskId, cardDraft, taskIdSiblingTasks, contractsById, workTypeLabel, stagesByContractId]);

  const familyAnchorContract = useMemo<Contract | null>(() => {
    if (displayedContract) return displayedContract;
    if (task?.contract_id != null) {
      return contractsById.get(task.contract_id) ?? null;
    }
    return null;
  }, [displayedContract, task?.contract_id, contractsById]);

  const contractFamily = useMemo(() => {
    if (!familyAnchorContract) return null;
    return resolveContractFamily(familyAnchorContract, allContracts);
  }, [familyAnchorContract, allContracts]);

  const familyContracts = useMemo(() => contractFamily?.familyContracts ?? [], [contractFamily]);

  const mainContract = useMemo(
    () => contractFamily?.mainContract ?? familyAnchorContract,
    [contractFamily, familyAnchorContract],
  );

  const familyContractor = useMemo(() => {
    if (!familyAnchorContract) return null;
    return contractorsById.get(familyAnchorContract.contractor_id) ?? null;
  }, [familyAnchorContract, contractorsById]);

  const familyTasks = useMemo(() => {
    if (!contractFamily) return [];
    return allTasks
      .filter((row) => contractFamily.familyIds.has(row.contract_id))
      .sort((left, right) => left.task_id.localeCompare(right.task_id, 'ru'));
  }, [allTasks, contractFamily]);

  const familyTasksSameId = useMemo(() => {
    if (!task?.task_id) return [];
    return familyTasks.filter((row) => row.task_id === task.task_id);
  }, [familyTasks, task?.task_id]);

  const familyTasksForHierarchy = useMemo(
    () =>
      [...familyTasks].sort((left, right) => {
        const byTaskId = left.task_id.localeCompare(right.task_id, 'ru');
        if (byTaskId !== 0) return byTaskId;
        return workTypeLabel(left.work_type_id).localeCompare(workTypeLabel(right.work_type_id), 'ru', {
          sensitivity: 'base',
        });
      }),
    [familyTasks, workTypeLabel],
  );

  useEffect(() => {
    if (familyContracts.length > 0) {
      setExpandedContractIds(new Set(familyContracts.map((ct) => ct.id)));
    }
  }, [familyContracts]);

  useEffect(() => {
    setDeadlinesTasksExpanded(false);
  }, [taskPk, task?.task_id]);

  const toggleDeadlinesTasks = useCallback((): void => {
    setDeadlinesTasksExpanded((prev) => !prev);
  }, []);

  const boundStagesForDeadlines = useMemo((): ContractStage[] => {
    const allStages = Object.values(stagesByContractId).flat();
    const stagesById = new Map(allStages.map((stage) => [stage.id, stage]));
    const boundIds = new Set<number>();
    for (const row of taskIdSiblingTasks) {
      const entry = cardEditing && cardDraft ? cardDraft.byContractsTask[row.contracts_task_id] : null;
      const stageIds = entry?.contract_stage_ids ?? row.contract_stage_ids ?? [];
      for (const stageId of stageIds) boundIds.add(stageId);
    }
    for (const stage of allStages) {
      if (stage.extends_stage_id != null && boundIds.has(stage.extends_stage_id)) {
        boundIds.add(stage.id);
      }
    }
    const bound: ContractStage[] = [];
    for (const stageId of boundIds) {
      const stage = stagesById.get(stageId);
      if (stage) bound.push(stage);
    }
    return bound;
  }, [taskIdSiblingTasks, cardEditing, cardDraft, stagesByContractId]);

  const taskIdDateRange = useMemo(() => {
    if (!task) {
      return { minStart: null as string | null, maxEnd: null as string | null };
    }
    if (boundStagesForDeadlines.length > 0) {
      return {
        minStart: minTaskDate(boundStagesForDeadlines.map((stage) => stage.planned_date_start)),
        maxEnd: maxTaskDate(boundStagesForDeadlines.map((stage) => stage.planned_date_end)),
      };
    }
    const starts = taskIdSiblingTasks.map((row) => {
      if (cardEditing && cardDraft) {
        const entry = cardDraft.byContractsTask[row.contracts_task_id];
        if (entry) return entry.deadlines.start_date.trim() || null;
      }
      return row.start_date?.trim() || null;
    });
    const ends = taskIdSiblingTasks.map((row) => {
      if (cardEditing && cardDraft) {
        const entry = cardDraft.byContractsTask[row.contracts_task_id];
        if (entry) return entry.deadlines.end_date.trim() || null;
      }
      return row.end_date?.trim() || null;
    });
    return {
      minStart: minTaskDate(starts),
      maxEnd: maxTaskDate(ends),
    };
  }, [task, taskIdSiblingTasks, cardEditing, cardDraft, boundStagesForDeadlines]);

  const taskIdExpenseClosingSummary = useMemo(
    () => resolveFamilyStageClosingSummary(boundStagesForDeadlines),
    [boundStagesForDeadlines],
  );

  const taskIdExpenseReferenceDate = useMemo(
    () => resolveFamilyStageReferenceDate(boundStagesForDeadlines),
    [boundStagesForDeadlines],
  );

  const deadline = useMemo(
    () => formatDeadlineRelativeTo(taskIdDateRange.maxEnd, taskIdExpenseReferenceDate),
    [taskIdDateRange.maxEnd, taskIdExpenseReferenceDate],
  );

  const peerAssigneesByDepartment = useMemo((): Map<number, PeerExecutorInDept[]> => {
    const result = new Map<number, PeerExecutorInDept[]>();
    if (!task) return result;

    for (const sibling of taskIdSiblingTasks) {
      if (sibling.contracts_task_id === activeContractsTaskId) continue;
      const byDept = assigneesGroupedByDepartment(sibling.assignee_employee_ids ?? [], employeesByDepartment);
      for (const [deptId, emps] of byDept) {
        const list = result.get(deptId) ?? [];
        for (const emp of emps) {
          if (!list.some((item) => item.employeeId === emp.id && item.taskPk === sibling.id)) {
            list.push({
              employeeId: emp.id,
              fullName: emp.full_name,
              taskPk: sibling.id,
              workType: workTypeLabel(sibling.work_type_id),
            });
          }
        }
        result.set(deptId, list);
      }
    }
    return result;
  }, [task, taskIdSiblingTasks, activeContractsTaskId, employeesByDepartment, workTypeLabel]);

  const closingStatuses = useMemo(
    () => (task?.closing_statuses ?? []).filter((row) => row.closing_level === 2),
    [task?.closing_statuses],
  );

  const taskTotalAmount = useMemo(
    () => taskIdSiblingTasks.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [taskIdSiblingTasks],
  );

  const receiptsTotal = task?.payment_paid ?? 0;
  const actualExpenses = useMemo(() => {
    if (planfactOperations.length > 0 && task) {
      return sumFamilyTaskActualExpensesFromOperations(planfactOperations, [task]);
    }
    return task?.actual_expenses ?? 0;
  }, [planfactOperations, task]);
  const balanceValue = receiptsTotal + actualExpenses;
  const receiptsRemainder = taskTotalAmount - receiptsTotal;
  const receiptsPercent = taskTotalAmount > 0
    ? Math.round((receiptsTotal / taskTotalAmount) * 100)
    : 0;
  const costPlan = useMemo(
    () => sumTasksPlannedExpenses(taskIdSiblingTasks),
    [taskIdSiblingTasks],
  );

  const financeRows = useMemo(
    () =>
      canBudgetTab
        ? buildMarginFinanceRows({ canSum: true, canCost: true, canMargin: true })
        : [],
    [canBudgetTab],
  );

  const budgetSiblingTasks = useMemo((): Task[] => {
    if (!task) return [];
    return sortMarginCalcColumns(taskIdSiblingTasks, contractsById, workTypeLabel);
  }, [task, taskIdSiblingTasks, contractsById, workTypeLabel]);

  const budgetHeaderCells = useMemo(
    () => buildMarginCalcHeaderCells(budgetSiblingTasks, contractsById, workTypeLabel),
    [budgetSiblingTasks, contractsById, workTypeLabel],
  );

  const budgetTableData = useMemo(() => {
    const editing = cardEditing && cardDraft != null;

    const storedValueForTaskColumn = (rowKey: string, columnTask: Task): number | null => {
      const financeKey = DRAFT_KEY_BY_ROW[rowKey];
      if (!financeKey) return null;
      if (editing && cardDraft) {
        const entry = cardDraft.byContractsTask[columnTask.contracts_task_id];
        if (entry) {
          return parseOptionalNumber(entry.finance[financeKey] ?? '') ?? null;
        }
      }
      return taskMarginFieldValue(rowKey, columnTask);
    };

    const otherBudgetRowKeys = financeRows
      .filter((row) => row.key !== 'amount' && !row.computed && row.draftKey)
      .map((row) => row.key);

    const valueForColumn = (rowKey: string, columnTask: Task): number | null => {
      if (rowKey === BUDGET_EXTRA_EXPENSES_KEY) {
        const amount = storedValueForTaskColumn('amount', columnTask);
        if (amount == null && !editing) {
          return null;
        }
        const othersSum = otherBudgetRowKeys.reduce(
          (sum, key) => sum + (storedValueForTaskColumn(key, columnTask) ?? 0),
          0,
        );
        return (amount ?? 0) - othersSum;
      }
      return storedValueForTaskColumn(rowKey, columnTask);
    };

    return buildMarginCalcTableData({
      financeRows,
      columnTasks: budgetSiblingTasks,
      valueForColumn,
    });
  }, [financeRows, budgetSiblingTasks, cardEditing, cardDraft]);

  const cashFlowExtras = ALL_TASK_EXPENSE_EXTRAS;

  const expenseExtras = useMemo(
    () => ({
      cash: canOutcomeCash,
      tax: canOutcomeTax,
      serviceFee: canOutcomeServ,
      partnerCommission: canOutcomePartner,
    }),
    [canOutcomeCash, canOutcomeTax, canOutcomeServ, canOutcomePartner],
  );

  const balanceTableData = useMemo(() => {
    if (!task?.task_id) {
      return null;
    }
    return buildTaskBalanceTable({
      operations: planfactOperations,
      articles,
      businessTaskId: task.task_id,
      extras: cashFlowExtras,
    });
  }, [task?.task_id, planfactOperations, articles, cashFlowExtras]);

  const expensesTableData = useMemo(() => {
    if (!task?.task_id) {
      return null;
    }
    return buildTaskExpensesTable({
      operations: planfactOperations,
      articles,
      businessTaskId: task.task_id,
      extras: expenseExtras,
      allowedPlanfactArticles: expenseArticleFilter,
    });
  }, [task?.task_id, planfactOperations, articles, expenseExtras, expenseArticleFilter]);

  const visibleActualExpenses = useMemo(() => {
    const fromTable = expensesTableData?.rows.find(
      (row) => row.id === TASK_BALANCE_EXPENSES_ROW_ID,
    )?.values.total;
    return fromTable ?? actualExpenses;
  }, [expensesTableData, actualExpenses]);

  const expensePlan = useMemo(
    () => (canOutcomePlan ? normalizeExpensePlan(costPlan) : null),
    [canOutcomePlan, costPlan],
  );
  const expensePlanRemainder = expensePlan != null ? expensePlan + visibleActualExpenses : null;

  const expensesBanner = useMemo(
    () => formatExpensesTabBanner(visibleActualExpenses, expensePlan),
    [visibleActualExpenses, expensePlan],
  );

  const taskOperationsForId = useMemo(() => {
    if (!task?.task_id) {
      return [];
    }
    return filterTaskOperations(planfactOperations, task.task_id);
  }, [planfactOperations, task?.task_id]);

  const displayedOperations = useMemo(() => {
    if (!task?.task_id) {
      return [];
    }
    const extras = activeCardTab === 'expenses' ? expenseExtras : cashFlowExtras;
    if (opsViewFilter.kind === 'balanceCell') {
      if (activeCardTab === 'expenses') {
        return collectOperationsForExpensesCell({
          operations: planfactOperations,
          articles,
          businessTaskId: task.task_id,
          rowId: opsViewFilter.rowId,
          columnKey: opsViewFilter.columnKey,
          extras,
          allowedPlanfactArticles: expenseArticleFilter,
        });
      }
      return collectOperationsForBalanceCell({
        operations: planfactOperations,
        articles,
        businessTaskId: task.task_id,
        rowId: opsViewFilter.rowId,
        columnKey: opsViewFilter.columnKey,
        extras,
      });
    }
    if (activeCardTab === 'expenses') {
      return excludeHiddenExpenseCategoryOperations(
        filterTaskOperations(planfactOperations, task.task_id).filter(
          (operation) =>
            isOperationExpense(operation) || operationContributesExpenseExtras(operation, extras),
        ),
        articles,
        extras,
        expenseArticleFilter,
      );
    }
    return excludeHiddenExpenseCategoryOperations(taskOperationsForId, articles, extras);
  }, [
    opsViewFilter,
    taskOperationsForId,
    task?.task_id,
    planfactOperations,
    articles,
    activeCardTab,
    expenseExtras,
    cashFlowExtras,
    expenseArticleFilter,
  ]);

  const opsFilterHint = useMemo((): string | null => {
    if (opsViewFilter.kind === 'balanceCell') {
      return `${opsViewFilter.rowLabel} · ${opsViewFilter.columnLabel}`;
    }
    return null;
  }, [opsViewFilter]);

  const handleBalanceCellClick = useCallback((cell: TaskBalanceCellClick): void => {
    setOpsViewFilter({
      kind: 'balanceCell',
      rowId: cell.rowId,
      rowLabel: cell.rowLabel,
      columnKey: cell.columnKey,
      columnLabel: cell.columnLabel,
    });
    window.requestAnimationFrame(() => {
      document.getElementById('task-detail-operations')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, []);

  const clearOpsFilter = useCallback((): void => {
    setOpsViewFilter({ kind: 'all' });
  }, []);

  const showOperationsBlock =
    (canBalanceTab && activeCardTab === 'balance')
    || (canExpensesTab && activeCardTab === 'expenses');

  const familyTasksSameIdForDeadlines = useMemo(() => {
    if (!cardEditing || !cardDraft) return familyTasksSameId;
    return familyTasksSameId.map((row) => {
      const entry = cardDraft.byContractsTask[row.contracts_task_id];
      if (!entry) return row;
      const allStages = Object.values(stagesByContractId).flat();
      const bound = allStages.filter((item) => entry.contract_stage_ids.includes(item.id));
      const dates = bound.length > 0 ? datesFromBoundStages(bound) : entry.deadlines;
      return {
        ...row,
        start_date: dates.start_date.trim(),
        end_date: dates.end_date.trim() || undefined,
      };
    });
  }, [familyTasksSameId, cardEditing, cardDraft, stagesByContractId]);

  const taskDeadlinesData = useMemo(() => {
    if (!task?.task_id) {
      return buildTaskIdDeadlinesData({
        tasks: [],
        taskId: '',
        stages: [],
      });
    }
    return buildTaskIdDeadlinesData({
      tasks: familyTasksSameIdForDeadlines,
      taskId: task.task_id,
      stages: boundStagesForDeadlines,
    });
  }, [task?.task_id, familyTasksSameIdForDeadlines, boundStagesForDeadlines]);

  const visibleDeadlinesData = useMemo(() => {
    if (deadlinesTasksExpanded) return taskDeadlinesData;
    return {
      ...taskDeadlinesData,
      tableRows: taskDeadlinesData.tableRows.filter((row) => row.isTaskIdRow || row.isTotal),
      ganttRows: taskDeadlinesData.ganttRows.filter((row) => row.tier === 'contract' || row.isTotal),
    };
  }, [taskDeadlinesData, deadlinesTasksExpanded]);

  const workTypeSelectOptions = useMemo(
    () => [...workTypes].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [workTypes],
  );

  const visibleTabs = useMemo((): Array<{ id: TaskCardTabId; label: string; warn?: boolean }> => {
    const tabs: Array<{ id: TaskCardTabId; label: string; warn?: boolean }> = [
      { id: 'general', label: 'Основное' },
      { id: 'executors', label: 'Исполнители' },
    ];
    if (canBudgetTab) {
      tabs.push({ id: 'budget', label: 'Расчет маржи' });
    }
    if (canExpensesTab) {
      tabs.push({
        id: 'expenses',
        label: 'Расходы',
        warn: expensesBanner.overPlan,
      });
    }
    if (canBalanceTab) {
      tabs.push({
        id: 'balance',
        label: 'Денежный Поток',
        warn: balanceValue < 0,
      });
    }
    tabs.push({
      id: 'deadlines',
      label: 'Сроки',
      warn: deadline.label != null && deadline.overdue,
    });
    return tabs;
  }, [canBudgetTab, canExpensesTab, canBalanceTab, expensesBanner.overPlan, balanceValue, deadline.label, deadline.overdue]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeCardTab)) {
      setActiveCardTab(visibleTabs[0]?.id ?? 'general');
    }
  }, [visibleTabs, activeCardTab]);

  if (!canTasks) {
    return (
      <div className="data-page">
        <div className="empty-state">Нет доступа к модулю задач (требуется право VIEW).</div>
      </div>
    );
  }

  if (Number.isNaN(taskPk)) {
    return (
      <div className="data-page">
        <div className="error-message">Некорректный идентификатор задачи</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="data-page">
        <div className="loading">Загрузка...</div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="data-page task-detail">
        <div className="error-message">{error || 'Задача не найдена'}</div>
      </div>
    );
  }

  const effectiveTaskManagerId = task.manager_id ?? displayedContract?.manager_id ?? null;
  const effectiveTaskProjectLeadId = task.project_lead_id ?? displayedContract?.project_lead_id ?? null;
  const taskManagerName = getEmployeeName(effectiveTaskManagerId);
  const taskProjectLeadName = getEmployeeName(effectiveTaskProjectLeadId);
  const editingActive = cardEditing && cardDraft != null;
  return (
    <div className="task-detail">
      <header className="task-detail__sticky">
        <div className="task-detail__header-main">
          {canEditTask ? (
            <input
              type="text"
              className="contractor-detail__name-field task-detail__name-field"
              value={headerName}
              onChange={(e) => {
                setHeaderName(e.target.value);
                setNameError('');
              }}
              onBlur={() => void handleNameBlur()}
              disabled={nameSaving}
              aria-label="Название задачи"
            />
          ) : (
            <h1 className="task-detail__title">{task.short_name}</h1>
          )}
          {nameSaving ? (
            <span className="contractor-detail__inline-hint" aria-live="polite">
              Сохранение…
            </span>
          ) : null}
        </div>
        {nameError ? <div className="contractor-detail__name-error">{nameError}</div> : null}

        <div className="task-detail__stats">
          <div className="task-detail__stat">
            <div className="task-detail__stat-title">Основная информация</div>
            <div className="task-detail__stat-body">
              <div className="task-detail__stat-id">{task.task_id}</div>
              <div className="task-detail__stat-kv">Вид работ: {taskWorkTypesLabel}</div>
              <div className="task-detail__stat-kv">Менеджер: {taskManagerName}</div>
              <div className="task-detail__stat-kv">РП: {taskProjectLeadName}</div>
            </div>
          </div>

          {canCashFlow ? (
            <div className="task-detail__stat">
              <div className="task-detail__stat-title">Остаток по поступлениям</div>
              <div className="task-detail__stat-body">
                <div className="task-detail__stat-row">
                  <span className="task-detail__stat-value">{formatContractSumDisplay(receiptsRemainder)}</span>
                  <PaymentDonut percent={receiptsPercent} tone="success" />
                </div>
                <div className="task-detail__stat-meta">
                  <div>всего: {formatContractSumDisplay(taskTotalAmount)}</div>
                  <div>поступило: {formatContractSumDisplay(receiptsTotal)}</div>
                </div>
              </div>
            </div>
          ) : null}

          {canOutcome ? (
            <div className="task-detail__stat">
              <div className="task-detail__stat-title">Расходы</div>
              <div className="task-detail__stat-body">
                <div className="task-detail__stat-row">
                  {expensePlan != null ? (
                    <>
                      <span
                        className={`task-detail__stat-value${
                          expensePlanRemainder != null && expensePlanRemainder > 0
                            ? ' task-detail__stat-value--positive'
                            : expensePlanRemainder != null && expensePlanRemainder < 0
                              ? ' task-detail__stat-value--negative'
                              : ''
                        }`}
                      >
                        {expensePlanRemainder != null ? formatSignedBalance(expensePlanRemainder) : '—'}
                      </span>
                      <BudgetProgressBar plan={expensePlan} remainder={expensePlanRemainder} />
                    </>
                  ) : (
                    <span className="task-detail__stat-value">
                      {formatFactExpensesTotal(visibleActualExpenses)}
                    </span>
                  )}
                </div>
                <div className="task-detail__stat-meta">
                  {expensePlan != null ? <div>план расходы: {formatFinanceCell(expensePlan)}</div> : null}
                  <div>факт. расходы: {formatFactExpensesTotal(visibleActualExpenses)}</div>
                </div>
              </div>
            </div>
          ) : null}

          {canCashFlow ? (
            <div className="task-detail__stat">
              <div className="task-detail__stat-title">Денежный Поток</div>
              <div className="task-detail__stat-body">
                <div className="task-detail__stat-row">
                  <span
                    className={`task-detail__stat-value${
                      balanceValue > 0
                        ? ' task-detail__stat-value--positive'
                        : balanceValue < 0
                          ? ' task-detail__stat-value--negative'
                          : ''
                    }`}
                  >
                    {formatSignedBalance(balanceValue)}
                  </span>
                  <BalanceBarChart receipts={receiptsTotal} expenses={actualExpenses} />
                </div>
                <div className="task-detail__stat-meta">
                  <div>поступления: {formatContractSumDisplay(receiptsTotal)}</div>
                  <div>факт. расходы: {formatFactExpensesTotal(actualExpenses)}</div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="task-detail__stat task-detail__stat--deadline">
            <div className="task-detail__stat-title">СРОКИ</div>
            <div className="task-detail__stat-body">
              {deadline.label ? (
                <div
                  className={`task-detail__deadline-heading${deadline.overdue ? ' task-detail__deadline-heading--overdue' : ''}`}
                >
                  {deadline.label}:
                </div>
              ) : null}
              <div
                className={`task-detail__deadline-counters${deadline.overdue ? ' task-detail__deadline-counters--overdue' : ''}`}
              >
                <div className="task-detail__deadline-unit">
                  <span className="task-detail__deadline-num">
                    {deadline.months != null ? deadline.months : '—'}
                  </span>
                  <span className="task-detail__deadline-label">мес.</span>
                </div>
                <div className="task-detail__deadline-unit">
                  <span className="task-detail__deadline-num">{deadline.days != null ? deadline.days : '—'}</span>
                  <span className="task-detail__deadline-label">дн.</span>
                </div>
              </div>
              <div className="task-detail__stat-meta">
                <div>крайний срок: {taskIdDateRange.maxEnd ? formatDate(taskIdDateRange.maxEnd) : '—'}</div>
                <div>
                  закрытие этапа:{' '}
                  {taskIdExpenseClosingSummary.allClosed && taskIdExpenseClosingSummary.maxClosingDate
                    ? formatDate(taskIdExpenseClosingSummary.maxClosingDate)
                    : '—'}
                </div>
              </div>
            </div>
          </div>

          <div className="task-detail__stat task-detail__stat--closing">
            <div className="task-detail__stat-title">Статусы закрытия</div>
            <div className="task-detail__stat-body task-detail__stat-body--closing">
              <span
                className={`task-detail__status-badge task-detail__status-badge--closing task-detail__status-badge--${taskStatusBadgeVariant(task.task_status)}`}
              >
                {getTaskStatusLabel(task.task_status)}
              </span>
              <ClosingStatusLines rows={closingStatuses} />
            </div>
          </div>
        </div>
      </header>

      <section className="contractor-detail__card task-detail__card">
        <div className="contractor-detail__card-head">
          <h2 className="contractor-detail__card-title">Карточка задачи</h2>
          {canEditTask ? (
            editingActive ? (
              <div className="contractor-detail__card-actions">
                <button
                  type="button"
                  className="submit-button"
                  disabled={cardSaving}
                  onClick={() => void handleSaveCardEdit()}
                >
                  {cardSaving ? 'Сохранение…' : 'Сохранить'}
                </button>
                <button type="button" className="secondary-button" disabled={cardSaving} onClick={handleCancelCardEdit}>
                  Отмена
                </button>
              </div>
            ) : (
              <button type="button" className="secondary-button" onClick={handleStartCardEdit}>
                Редактировать
              </button>
            )
          ) : null}
        </div>
        {cardError ? <div className="contractor-detail__card-error">{cardError}</div> : null}

        <div className="contractor-detail__tabs" role="tablist">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeCardTab === tab.id}
              className={`contractor-detail__tab${activeCardTab === tab.id ? ' contractor-detail__tab--active' : ''}${tab.warn ? ' contractor-detail__tab--warn' : ''}`}
              onClick={() => {
                setActiveCardTab(tab.id);
                setOpsViewFilter({ kind: 'all' });
              }}
            >
              {tab.warn ? (
                <span className="contract-detail__tab-warn-dot" aria-hidden>
                  !
                </span>
              ) : null}
              {tab.label}
            </button>
          ))}
        </div>

        {activeCardTab === 'general' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            <div className="contract-detail__general-layout">
              <div className="contract-detail__general-leaders-row contract-detail__general-leaders-row--3">
                <div className="task-executors__block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">Контрагент</span>
                    </span>
                  </div>
                  <div className="task-executors__list">
                    {canContractors && familyContractor ? (
                      <button
                        type="button"
                        className="task-executors__person task-detail__link contract-detail__general-contractor"
                        onClick={() => navigate(`/contractors/${familyContractor.id}`)}
                      >
                        {familyContractor.name}
                      </button>
                    ) : (
                      <div className="task-executors__person">{familyContractor?.name ?? '—'}</div>
                    )}
                  </div>
                </div>
                <div className="task-executors__block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">Менеджер</span>
                    </span>
                  </div>
                  <div className="task-executors__list">
                    {editingActive && cardDraft ? (
                      <select
                        className="contractor-detail__field contract-detail__general-select"
                        value={cardDraft.manager_id == null ? '' : String(cardDraft.manager_id)}
                        disabled={cardSaving}
                        onChange={(e) => {
                          setCardDraft({
                            ...cardDraft,
                            manager_id: e.target.value === '' ? null : Number(e.target.value),
                          });
                          if (cardError) setCardError('');
                        }}
                        aria-label="Менеджер"
                      >
                        <option value="">—</option>
                        {managerSelectOptions.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.full_name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="task-executors__person">{taskManagerName}</div>
                    )}
                  </div>
                </div>
                <div className="task-executors__block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">РП</span>
                    </span>
                  </div>
                  <div className="task-executors__list">
                    {editingActive && cardDraft ? (
                      <select
                        className="contractor-detail__field contract-detail__general-select"
                        value={cardDraft.project_lead_id == null ? '' : String(cardDraft.project_lead_id)}
                        disabled={cardSaving}
                        onChange={(e) => {
                          setCardDraft({
                            ...cardDraft,
                            project_lead_id: e.target.value === '' ? null : Number(e.target.value),
                          });
                          if (cardError) setCardError('');
                        }}
                        aria-label="РП"
                      >
                        <option value="">—</option>
                        {projectLeadSelectOptions.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.full_name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="task-executors__person">{taskProjectLeadName}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="contract-detail__general-fields">
                <div className="task-executors__block contract-detail__general-block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">Иерархия договора</span>
                    </span>
                  </div>
                  <div className="contract-detail__general-block-body">
                    {!familyAnchorContract || familyContracts.length === 0 ? (
                      <p className="task-detail__hint">Договор задачи недоступен в вашей выборке.</p>
                    ) : (
                      <div className="task-detail__accordion task-detail__accordion--hierarchy contract-detail__subject-hierarchy">
                        <table className="contract-detail__general-table">
                          <tbody>
                            {familyContracts.map((ct) => {
                              const contractExpanded = expandedContractIds.has(ct.id);
                              const contractTaskLinks = familyTasksForHierarchy
                                .filter((row) => row.contract_id === ct.id)
                                .sort((a, b) => a.task_id.localeCompare(b.task_id, 'ru'));
                              const isCurrent = ct.id === displayedContractId;
                              return (
                                <React.Fragment key={ct.id}>
                                  <tr
                                    className={`contract-row${isCurrent ? ' task-row--current' : ''}`}
                                    onClick={() => toggleContract(ct.id)}
                                  >
                                    <td className="contract-detail__general-table-label">
                                      <span className="task-detail__chevron" aria-hidden>
                                        {contractExpanded ? '▼' : '▶'}
                                      </span>{' '}
                                      {ct.hierarchy_level === 2 ? (
                                        <span className="contract-detail__family-tree-mark" aria-hidden>
                                          └──{' '}
                                        </span>
                                      ) : null}
                                      <button
                                        type="button"
                                        className="task-detail__link"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/contracts/${ct.id}`);
                                        }}
                                      >
                                        <strong>{ct.contract_number}</strong>
                                      </button>
                                    </td>
                                    <td
                                      className="contract-detail__general-table-value"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="contract-detail__general-field contract-detail__general-field--readonly">
                                        {ct.contract_subject?.trim() ? ct.contract_subject : '—'}
                                      </div>
                                    </td>
                                    <td
                                      className="contract-detail__general-table-original"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <span
                                        className={`contract-detail__family-sign${ct.has_original ? ' contract-detail__family-sign--ok' : ' contract-detail__family-sign--no'}`}
                                        title={
                                          ct.has_original
                                            ? 'Оригинал подписан'
                                            : 'Оригинал не подписан'
                                        }
                                        aria-label={
                                          ct.has_original
                                            ? 'Оригинал подписан'
                                            : 'Оригинал не подписан'
                                        }
                                      >
                                        {ct.has_original ? '✓' : '✗'}
                                      </span>
                                    </td>
                                  </tr>
                                  {contractExpanded &&
                                    (contractTaskLinks.length === 0 ? (
                                      <tr className="task-row" key={`${ct.id}-empty`}>
                                        <td
                                          colSpan={3}
                                          className="task-detail__muted-text task-detail__muted-text--italic"
                                        >
                                          Нет связей задачи с договором
                                        </td>
                                      </tr>
                                    ) : (
                                      contractTaskLinks.map((tk) => {
                                        const draftEntry = cardDraft?.byContractsTask[tk.contracts_task_id];
                                        const isEditableCurrentTaskId = tk.task_id === task.task_id;
                                        const workTypeId =
                                          editingActive && isEditableCurrentTaskId && draftEntry
                                            ? draftEntry.work_type_id
                                            : tk.work_type_id;
                                        const isCurrentTask =
                                          tk.contracts_task_id === activeContractsTaskId;
                                        return (
                                          <tr
                                            key={tk.contracts_task_id}
                                            className={`task-row${isCurrentTask ? ' task-row--current' : ''}`}
                                            onClick={
                                              editingActive
                                                ? undefined
                                                : () => {
                                                    if (!isCurrentTask) {
                                                      navigate(taskDetailPath(tk));
                                                    }
                                                  }
                                            }
                                            style={editingActive || isCurrentTask ? undefined : { cursor: 'pointer' }}
                                          >
                                            <td colSpan={3} onClick={(e) => editingActive && e.stopPropagation()}>
                                              {tk.task_id}:{' '}
                                              {editingActive && cardDraft && draftEntry && isEditableCurrentTaskId ? (
                                                <select
                                                  className="contractor-detail__field task-detail__hierarchy-work-type-select"
                                                  value={workTypeId == null ? '' : String(workTypeId)}
                                                  disabled={cardSaving}
                                                  onClick={(e) => e.stopPropagation()}
                                                  onChange={(e) => {
                                                    const nextId =
                                                      e.target.value === '' ? null : Number(e.target.value);
                                                    setCardDraft({
                                                      ...cardDraft,
                                                      byContractsTask: {
                                                        ...cardDraft.byContractsTask,
                                                        [tk.contracts_task_id]: {
                                                          ...draftEntry,
                                                          work_type_id: nextId,
                                                        },
                                                      },
                                                    });
                                                    if (cardError) setCardError('');
                                                  }}
                                                  aria-label={`Вид работ ${tk.task_id}`}
                                                >
                                                  <option value="">—</option>
                                                  {workTypeSelectOptions.map((wt) => (
                                                    <option key={wt.id} value={wt.id}>
                                                      {wt.name}
                                                    </option>
                                                  ))}
                                                </select>
                                              ) : (
                                                workTypeLabel(workTypeId)
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })
                                    ))}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
                <div className="task-executors__block contract-detail__general-block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">Адрес</span>
                    </span>
                  </div>
                  <div className="contract-detail__general-block-body">
                    <table className="contract-detail__general-table">
                      <tbody>
                        <tr>
                          <th scope="row" className="contract-detail__general-table-label">
                            Адрес
                          </th>
                          <td className="contract-detail__general-table-value">
                            <div className="contract-detail__general-field contract-detail__general-field--readonly">
                              {mainContract?.address?.trim() ? mainContract.address : '—'}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row" className="contract-detail__general-table-label">
                            к.н.
                          </th>
                          <td className="contract-detail__general-table-value">
                            <div className="contract-detail__general-field contract-detail__general-field--readonly">
                              {mainContract?.cadastral_number?.trim()
                                ? mainContract.cadastral_number
                                : '—'}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeCardTab === 'deadlines' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            {visibleDeadlinesData.tableRows.length === 0 ? (
              <p className="contractor-detail__muted">Нет данных о сроках.</p>
            ) : (
              <>
                <div
                  className={`contract-detail__deadlines-banner${deadline.overdue ? ' contract-detail__deadlines-banner--overdue' : deadline.label ? ' contract-detail__deadlines-banner--on-time' : ' contract-detail__deadlines-banner--neutral'}`}
                >
                  <div className="contract-detail__deadlines-banner-main">
                    {deadline.label ? (
                      <div
                        className={`contract-detail__deadlines-banner-heading${deadline.overdue ? ' contract-detail__deadlines-banner-heading--overdue' : ''}`}
                      >
                        {deadline.label}
                      </div>
                    ) : (
                      <div className="contract-detail__deadlines-banner-heading">Сроки</div>
                    )}
                    {deadline.label ? (
                      <div
                        className={`contract-detail__deadlines-banner-counters${deadline.overdue ? ' contract-detail__deadlines-banner-counters--overdue' : ''}`}
                      >
                        <span className="contract-detail__deadlines-banner-num">
                          {deadline.months != null ? deadline.months : '—'}
                        </span>
                        <span className="contract-detail__deadlines-banner-unit">мес.</span>
                        <span className="contract-detail__deadlines-banner-num">
                          {deadline.days != null ? deadline.days : '—'}
                        </span>
                        <span className="contract-detail__deadlines-banner-unit">дн.</span>
                      </div>
                    ) : null}
                  </div>
                  <div className="contract-detail__deadlines-banner-meta">
                    <span>Крайний срок: {taskIdDateRange.maxEnd ? formatDate(taskIdDateRange.maxEnd) : '—'}</span>
                    <span>
                      Закрытие этапа:{' '}
                      {taskDeadlinesData.familyExpenseClosingDisplay !== '—'
                        ? taskDeadlinesData.familyExpenseClosingDisplay
                        : 'не завершено'}
                    </span>
                  </div>
                </div>
                <p className="contract-detail__deadlines-summary">
                  {visibleDeadlinesData.summary.contractCount} дог.
                  {' · '}
                  {visibleDeadlinesData.summary.taskCount} этапов
                  {visibleDeadlinesData.summary.overdueTaskCount > 0 ? (
                    <>
                      {' · '}
                      <span className="contract-detail__deadlines-summary-warn">
                        {visibleDeadlinesData.summary.overdueTaskCount} с просрочкой
                      </span>
                    </>
                  ) : null}
                  {visibleDeadlinesData.summary.closedOnTimeCount > 0 ? (
                    <>
                      {' · '}
                      {visibleDeadlinesData.summary.closedOnTimeCount} закрыто в срок
                    </>
                  ) : null}
                </p>
                {editingActive && cardDraft ? (
                  <div className="contract-stages__task-binds">
                    {taskIdSiblingTasks.map((row) => {
                      const entry = cardDraft.byContractsTask[row.contracts_task_id];
                      if (!entry) return null;
                      const stages = stagesByContractId[entry.contract_id] ?? [];
                      const contract = contractsById.get(entry.contract_id);
                      return (
                        <div key={row.contracts_task_id} className="contract-stages__task-bind-card">
                          <h3 className="contract-stages__task-bind-title">
                            {contract?.contract_number ?? 'Договор'} · {workTypeLabel(entry.work_type_id ?? row.work_type_id)}
                          </h3>
                          {stages.length > 0 ? (
                            <StageBindPicker
                              options={stages.map((stage) => ({
                                id: String(stage.id),
                                label: `${stage.stage_number}. ${stage.name}`,
                              }))}
                              selectedIds={entry.contract_stage_ids.map(String)}
                              onChange={(ids) => {
                                setCardDraft({
                                  ...cardDraft,
                                  byContractsTask: {
                                    ...cardDraft.byContractsTask,
                                    [row.contracts_task_id]: {
                                      ...entry,
                                      contract_stage_ids: ids
                                        .map(Number)
                                        .filter((id) => Number.isFinite(id)),
                                    },
                                  },
                                });
                                if (cardError) setCardError('');
                              }}
                              canEdit
                              disabled={cardSaving}
                            />
                          ) : (
                            <p className="contractor-detail__muted">
                              У договора нет этапов работ — сроки задаются вручную.
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
                <div className="task-detail__deadlines-layout">
                  <table className="task-detail__mini-table task-detail__mini-table--compact task-detail__deadlines-table task-detail__deadlines-table--levels task-detail__deadlines-table--grouped task-detail__deadlines-table--task-id contract-detail__deadlines-table">
                    <thead>
                      <tr className="task-detail__deadlines-group-row">
                        <th colSpan={2} className="task-detail__deadlines-group-spacer" />
                        <th colSpan={2} className="task-detail__deadlines-group-head task-detail__deadlines-group-head--plan">
                          План
                        </th>
                        <th colSpan={2} className="task-detail__deadlines-group-head task-detail__deadlines-group-head--fact">
                          Факт
                        </th>
                      </tr>
                      <tr>
                        <th className="task-detail__deadlines-level-col task-detail__deadlines-level-col--1">
                          ID
                        </th>
                        <th className="task-detail__deadlines-level-col task-detail__deadlines-level-col--3">
                          Этап
                        </th>
                        <th className="task-detail__deadlines-date-head">Начало</th>
                        <th className="task-detail__deadlines-date-head">Крайний срок</th>
                        <th className="task-detail__deadlines-date-head">Закрытие этапа</th>
                        <th className="task-detail__deadlines-date-head task-detail__deadlines-delta-head">Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleDeadlinesData.tableRows.map((row) => (
                          <tr
                            key={row.key}
                            className={[
                              row.isTotal ? 'task-detail__deadlines-row--total' : '',
                              row.isTaskIdRow ? 'task-detail__deadlines-row--contract' : '',
                              row.isStageRow ? 'task-detail__deadlines-row--task' : '',
                            ]
                              .filter(Boolean)
                              .join(' ') || undefined}
                          >
                            {row.isTotal ? (
                              <td
                                colSpan={2}
                                className="task-detail__deadlines-level-col task-detail__deadlines-level-col--3 task-detail__deadlines-total-label"
                              >
                                <strong>Итого</strong>
                              </td>
                            ) : row.isTaskIdRow ? (
                              <>
                                <td className="task-detail__deadlines-level-col task-detail__deadlines-level-col--1 task-detail__td-text">
                                  <button
                                    type="button"
                                    className="contract-detail__deadlines-expand-btn"
                                    onClick={toggleDeadlinesTasks}
                                    aria-expanded={deadlinesTasksExpanded}
                                    aria-label={deadlinesTasksExpanded ? 'Скрыть этапы' : 'Показать этапы'}
                                  >
                                    <span className="task-detail__chevron" aria-hidden>
                                      {deadlinesTasksExpanded ? '▼' : '▶'}
                                    </span>
                                  </button>{' '}
                                  <strong>{row.taskId}</strong>
                                </td>
                                <td className="task-detail__deadlines-level-col task-detail__deadlines-level-col--3" />
                              </>
                            ) : (
                              <>
                                <td className="task-detail__deadlines-level-col task-detail__deadlines-level-col--1" />
                                <td className="task-detail__deadlines-level-col task-detail__deadlines-level-col--3 task-detail__td-text">
                                  {row.stageLabel}
                                </td>
                              </>
                            )}
                            <td className="task-detail__deadlines-date">
                              {formatDateShort(row.startDate)}
                            </td>
                            <td className="task-detail__deadlines-date">
                              {formatDateShort(row.endDate)}
                            </td>
                            <td className="task-detail__deadlines-date task-detail__deadlines-closing-cell">
                              {row.isTotal ? <strong>{row.expenseClosingDisplay}</strong> : row.expenseClosingDisplay}
                            </td>
                            <td className={deadlineDeltaCellClass()}>
                              {row.isTaskIdRow || row.isTotal ? (
                                <span className={deadlineStatusBadgeClass(row.deltaVariant)}>
                                  {row.deltaDisplay}
                                </span>
                              ) : row.isStageRow ? (
                                row.deltaDisplay
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="task-detail__deadlines-chart-wrap">
                    <TaskDeadlinesGantt
                      className="task-deadlines-gantt--contract-deadlines"
                      rows={visibleDeadlinesData.ganttRows}
                      spanStartMs={visibleDeadlinesData.spanStartMs}
                      spanEndMs={visibleDeadlinesData.spanEndMs}
                      axisStartLabel={visibleDeadlinesData.axisStartLabel}
                      axisEndLabel={visibleDeadlinesData.axisEndLabel}
                      totalOverdue={visibleDeadlinesData.totalOverdue}
                      todayMs={visibleDeadlinesData.todayMs}
                      referenceMarkerMs={visibleDeadlinesData.referenceMarkerMs}
                      referenceMarkerLabel={visibleDeadlinesData.referenceMarkerLabel}
                      showGlobalReferenceMarker={visibleDeadlinesData.summary.allExpensesClosed}
                      showTodayMarker={!visibleDeadlinesData.summary.allExpensesClosed}
                      showLegend
                      alignGroupedHeader
                      showTimelineHeader
                      closingLegendLabel="закрытие этапа бухгалтерией"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeCardTab === 'executors' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            <div className="task-detail__executors-layout">
              <div className="task-detail__executors-grid-wrap">
                {editingActive && cardDraft ? (
                  <TaskExecutorsPanel
                    departments={executorDepartments}
                    employeesByDepartment={employeesByDepartment}
                    value={executorsFromDraft(cardDraft)}
                    onChange={(next) =>
                      setCardDraft({
                        ...cardDraft,
                        department_ids: next.department_ids,
                        assignee_employee_ids: next.assignee_employee_ids,
                      })
                    }
                    disabled={cardSaving}
                    peerAssigneesByDepartment={peerAssigneesByDepartment}
                  />
                ) : (
                  <TaskExecutorsPanel
                    departments={executorDepartments}
                    employeesByDepartment={employeesByDepartment}
                    value={{
                      department_ids: task.department_ids ?? [],
                      assignee_employee_ids: task.assignee_employee_ids ?? [],
                    }}
                    onChange={() => undefined}
                    disabled
                    peerAssigneesByDepartment={peerAssigneesByDepartment}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {activeCardTab === 'budget' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            <MarginCalcTable
              columnTasks={budgetSiblingTasks}
              headerCells={budgetHeaderCells}
              tableData={budgetTableData}
              editable={editingActive}
              getDraftValue={(contractsTaskId, financeKey) => {
                if (!cardDraft) return '';
                const draftKey = DRAFT_KEY_BY_ROW[financeKey] ?? (financeKey as FinanceDraftKey);
                return cardDraft.byContractsTask[contractsTaskId]?.finance[draftKey] ?? '';
              }}
              onDraftChange={(contractsTaskId, financeKey, value) => {
                if (!cardDraft) return;
                const draftKey = DRAFT_KEY_BY_ROW[financeKey] ?? (financeKey as FinanceDraftKey);
                setCardDraft({
                  ...cardDraft,
                  byContractsTask: {
                    ...cardDraft.byContractsTask,
                    [contractsTaskId]: {
                      ...cardDraft.byContractsTask[contractsTaskId],
                      finance: {
                        ...cardDraft.byContractsTask[contractsTaskId].finance,
                        [draftKey]: normalizeContractSumDraft(value),
                      },
                    },
                  },
                });
              }}
            />
          </div>
        )}

        {activeCardTab === 'expenses' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            <div
              className={`contract-detail__deadlines-banner${
                expensesBanner.kind === 'spent-only'
                  ? ' contract-detail__deadlines-banner--neutral'
                  : expensesBanner.overPlan
                    ? ' contract-detail__deadlines-banner--overdue'
                    : ' contract-detail__deadlines-banner--on-time'
              }`}
            >
              <div className="contract-detail__deadlines-banner-main">
                <div
                  className={`contract-detail__deadlines-banner-heading task-detail__expenses-banner-heading${
                    expensesBanner.overPlan ? ' contract-detail__deadlines-banner-heading--overdue' : ''
                  }`}
                >
                  <span>Всего потрачено</span>
                  <span className="task-detail__expenses-banner-value">
                    <span className="contract-detail__deadlines-banner-num">{expensesBanner.spent}</span>.
                  </span>
                  {expensesBanner.kind === 'spent-only' ? (
                    <span>Плана нет, не с чем сравнить.</span>
                  ) : null}
                  {expensesBanner.kind === 'equal' ? <span>Это соответствует плану.</span> : null}
                  {expensesBanner.kind === 'diff' ? (
                    <>
                      <span>Это на</span>
                      <span className="contract-detail__deadlines-banner-num">{expensesBanner.diff}</span>
                      {expensesBanner.percent ? (
                        <span className="contract-detail__deadlines-banner-num">
                          ({expensesBanner.percent})
                        </span>
                      ) : null}
                      <span>{expensesBanner.direction} плана.</span>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            {!expensesTableData ? (
              <p className="contractor-detail__muted">Нет данных по расходам.</p>
            ) : (
              <TaskBalanceTable
                data={expensesTableData}
                onCellClick={handleBalanceCellClick}
                showPlanColumns={expensePlan != null}
              />
            )}
          </div>
        )}

        {activeCardTab === 'balance' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            <div
              className={`contract-detail__deadlines-banner${
                balanceValue < 0
                  ? ' contract-detail__deadlines-banner--overdue'
                  : ' contract-detail__deadlines-banner--on-time'
              }`}
            >
              <div className="contract-detail__deadlines-banner-main">
                <div
                  className={`contract-detail__deadlines-banner-heading${
                    balanceValue < 0 ? ' contract-detail__deadlines-banner-heading--overdue' : ''
                  }`}
                >
                  {balanceValue < 0
                    ? 'Расходы превышают поступления на'
                    : 'Поступления превышают расходы на'}
                </div>
                <div
                  className={`contract-detail__deadlines-banner-counters${
                    balanceValue < 0 ? ' contract-detail__deadlines-banner-counters--overdue' : ''
                  }`}
                >
                  <span className="contract-detail__deadlines-banner-num">
                    {formatContractSumDisplay(Math.abs(balanceValue))}
                  </span>
                  <span className="contract-detail__deadlines-banner-unit">руб.</span>
                </div>
              </div>
              <div className="contract-detail__deadlines-banner-meta">
                <span>поступления: {formatContractSumDisplay(receiptsTotal)}</span>
                <span>факт. расходы: {formatFactExpensesTotal(actualExpenses)}</span>
              </div>
            </div>
            {!balanceTableData ? (
              <p className="contractor-detail__muted">Нет данных для баланса.</p>
            ) : (
              <TaskBalanceTable data={balanceTableData} onCellClick={handleBalanceCellClick} />
            )}
          </div>
        )}

      </section>

      {showOperationsBlock && task.task_id ? (
        <TaskOperationsTable
          id="task-detail-operations"
          operations={displayedOperations}
          filterHint={opsFilterHint}
          onClearFilter={opsViewFilter.kind === 'balanceCell' ? clearOpsFilter : undefined}
          extraColumns={
            activeCardTab === 'expenses'
              ? {
                  cash: canOutcomeCash,
                  tax: canOutcomeTax,
                  servFee: canOutcomeServ,
                }
              : undefined
          }
        />
      ) : null}
    </div>
  );
};

export default TaskDetailPage;
